import { useState } from "react";
import { toast } from "sonner";
import { Mail, Phone, MapPin, Instagram, Facebook, Music2 } from "lucide-react";
import { BRAND } from "@/content";
import { submitContact } from "@/api";

const initial = { name: "", email: "", subject: "", message: "" };

export default function Contact() {
  const [form, setForm] = useState(initial);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(false);

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please complete name, email, and message.");
      return;
    }
    setSending(true);
    try {
      await submitContact(form);
      setDone(true); setForm(initial);
      toast.success("Message sent.");
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally { setSending(false); }
  };

  return (
    <div data-testid="page-contact" className="bg-white">
      <section className="pt-[120px] pb-16">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10 items-end">
          <div className="col-span-12 lg:col-span-8">
            <p className="overline">Contact</p>
            <h1 className="display mt-6 text-[44px] md:text-[120px]">
              Let's <em className="font-serif text-[var(--ic-accent)]">talk</em>.
            </h1>
          </div>
          <div className="col-span-12 lg:col-span-4">
            <p className="font-sans text-[17px] leading-[1.75] text-black/75">
              General inquiries, press, partnerships, or just a hello. I personally read
              everything that comes through.
            </p>
          </div>
        </div>
      </section>

      <section className="py-20 lg:py-28 border-y border-[var(--ic-line)] bg-[var(--ic-warm)]">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 md:col-span-4">
            <p className="overline">Direct</p>
            <ul className="mt-6 space-y-4 text-[16px]">
              <li className="flex items-center gap-3"><Mail size={18}/> <a href={`mailto:${BRAND.email}`} className="hover:underline">{BRAND.email}</a></li>
              <li className="flex items-center gap-3"><Phone size={18}/> <a href={`tel:${BRAND.phone}`} className="hover:underline">{BRAND.phone}</a></li>
              <li className="flex items-center gap-3"><MapPin size={18}/> {BRAND.city}</li>
            </ul>
          </div>
          <div className="col-span-12 md:col-span-4">
            <p className="overline">Bookings</p>
            <ul className="mt-6 space-y-4 text-[16px]">
              <li><a href="/speaking" className="ed-link text-[12px]">Speaking inquiry</a></li>
              <li><a href="/workshops" className="ed-link text-[12px]">Workshop request</a></li>
              <li><a href={BRAND.calendly} target="_blank" rel="noreferrer" className="ed-link text-[12px]">Coaching consultation</a></li>
              <li><a href={BRAND.shop} target="_blank" rel="noreferrer" className="ed-link text-[12px]">Shop on Square</a></li>
            </ul>
          </div>
          <div className="col-span-12 md:col-span-4">
            <p className="overline">Follow</p>
            <div className="mt-6 flex gap-3">
              <a aria-label="Instagram" href={BRAND.social.instagram} target="_blank" rel="noreferrer" className="p-3 border border-black/15 hover:bg-black hover:text-white transition-colors"><Instagram size={16}/></a>
              <a aria-label="TikTok"    href={BRAND.social.tiktok}    target="_blank" rel="noreferrer" className="p-3 border border-black/15 hover:bg-black hover:text-white transition-colors"><Music2 size={16}/></a>
              <a aria-label="Facebook"  href={BRAND.social.facebook}  target="_blank" rel="noreferrer" className="p-3 border border-black/15 hover:bg-black hover:text-white transition-colors"><Facebook size={16}/></a>
            </div>
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32">
        <div className="mx-auto max-w-[1400px] px-6 lg:px-10 grid grid-cols-12 gap-10">
          <div className="col-span-12 lg:col-span-4">
            <p className="overline">Message Crystal</p>
            <h2 className="section-display mt-4">Send a note.</h2>
            <p className="font-sans text-[15px] text-black/65 mt-6 max-w-sm">
              We respond within 2 business days. For press and media, please mention "Press"
              in the subject line.
            </p>
          </div>

          <div className="col-span-12 lg:col-span-8">
            {done ? (
              <div className="border border-[var(--ic-line)] p-10" data-testid="contact-success">
                <p className="overline">Message received</p>
                <h3 className="font-serif text-[34px] mt-4">Your message landed safely.</h3>
                <p className="font-sans text-[16px] text-black/70 mt-4 max-w-xl">
                  Thank you for reaching out. We'll be in touch soon.
                </p>
                <button className="ed-link mt-8" onClick={() => setDone(false)}>Send another message</button>
              </div>
            ) : (
              <form onSubmit={submit} className="grid grid-cols-12 gap-6" data-testid="contact-form">
                <FieldText col={6} label="Name *"   v={form.name} on={onChange("name")} testid="ct-name"/>
                <FieldText col={6} label="Email *"  v={form.email} on={onChange("email")} type="email" testid="ct-email"/>
                <FieldText col={12} label="Subject" v={form.subject} on={onChange("subject")} testid="ct-subject"/>
                <FieldArea col={12} label="Message *" v={form.message} on={onChange("message")} testid="ct-message"/>
                <div className="col-span-12">
                  <button type="submit" disabled={sending} data-testid="ct-submit" className="btn-ink disabled:opacity-50">
                    {sending ? "Sending…" : "Send Message"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function FieldText({ col=12, label, v, on, type="text", testid }) {
  return (
    <div className={`col-span-12 md:col-span-${col}`}>
      <label className="ed-label block">{label}</label>
      <input data-testid={testid} type={type} value={v} onChange={on} className="ed-input" />
    </div>
  );
}
function FieldArea({ col=12, label, v, on, testid }) {
  return (
    <div className={`col-span-12 md:col-span-${col}`}>
      <label className="ed-label block">{label}</label>
      <textarea data-testid={testid} value={v} onChange={on} className="ed-textarea" rows={6} />
    </div>
  );
}
